

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Autocomplete,
    CircularProgress,
    IconButton,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewCMSPublisherModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance, user } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        title: '',
        target_section: 'Section1',
        image_url: '',
        video_url: '',
        trip_id: '',
        tags: [],
        on_hero: false,
        content: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [trips, setTrips] = useState([]);
    const [loadingTrips, setLoadingTrips] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchTrips();
        }
    }, [isOpen]);

    const fetchTrips = async () => {
        setLoadingTrips(true);
        try {
            const response = await axiosInstance.get('/trips/');
            setTrips(response.data);
        } catch (error) {
            console.error('Error fetching trips:', error);
        } finally {
            setLoadingTrips(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setFormErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleTagChange = (event, newValue) => {
        setFormData(prev => ({ ...prev, tags: newValue }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.title) errors.title = 'Title is required';
        if (!formData.trip_id) errors.trip_id = 'Trip is required';
        if (!formData.target_section) errors.target_section = 'Target Section is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                trip_id: parseInt(formData.trip_id),
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            // Reset form
            setFormData({
                name: '', title: '', target_section: 'Section1', image_url: '',
                video_url: '', trip_id: '', tags: [], on_hero: false, content: ''
            });
        } catch (error) {
            console.error('Error saving CMS Publisher:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving CMS Publisher',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '700px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New CMS Publisher</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Title"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        error={!!formErrors.title}
                                        helperText={formErrors.title}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.trip_id} required>
                                        <InputLabel>Trip</InputLabel>
                                        <Select
                                            name="trip_id"
                                            value={formData.trip_id}
                                            onChange={handleInputChange}
                                            label="Trip"

                                        >
                                            <MenuItem value=""><em>Select a Trip</em></MenuItem>
                                            {trips.map(t => (
                                                <MenuItem key={t.id} value={t.id}>{t.name} ({t.title})</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.trip_id && <MDTypography variant="caption" color="error">{formErrors.trip_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" required>
                                        <InputLabel>Target Section</InputLabel>
                                        <Select
                                            name="target_section"
                                            value={formData.target_section}
                                            onChange={handleInputChange}
                                            label="Target Section"

                                        >
                                            <MenuItem value="Hero">Hero</MenuItem>
                                            <MenuItem value="Section1">Section 1</MenuItem>
                                            <MenuItem value="Section2">Section 2</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Image URL"
                                        name="image_url"
                                        value={formData.image_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Video URL"
                                        name="video_url"
                                        value={formData.video_url}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        multiple
                                        options={['Soldout', 'FillingFast', 'Sold', 'New']}
                                        value={formData.tags}
                                        onChange={handleTagChange}
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label="Tags" 
                                                placeholder="Add tags" 
                                                variant="outlined"
                                                margin="normal"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        height: '35px',
                                                        alignItems: 'center'
                                                    },
                                                    '& .MuiInputLabel-outlined': {
                                                        transform: 'translate(14px, 12px) scale(1)'
                                                    },
                                                    '& .MuiInputLabel-shrink': {
                                                        transform: 'translate(14px, -6px) scale(0.75)'
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.on_hero} name="on_hero" onChange={handleInputChange} />}
                                        label="Display on Hero"
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Content"
                                        name="content"
                                        multiline
                                        rows={4}
                                        value={formData.content}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save CMS Publisher'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewCMSPublisherModal;
